import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileDown, FileText, ImagePlus, Loader2, Save } from "lucide-react";
import { api, fmt, fmtDate, apiError, downloadInvoicePdf } from "@/lib/api";
import { PaymentChip } from "@/pages/AccountInvoicesPage";

const inputCls = "h-11 w-full rounded-xl border border-white/10 bg-black/40 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";
const labelCls = "mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500";

function Field({ label, testid, hint, ...props }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input data-testid={testid} className={inputCls} {...props} />
      {hint && <p className="mt-1 text-[10px] text-neutral-600">{hint}</p>}
    </div>
  );
}

export default function AdminInvoices() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [downloading, setDownloading] = useState("");

  const load = useCallback(() => {
    api.get("/admin/invoices/settings").then(({ data }) => setForm(data)).catch((e) => toast.error(apiError(e, "Couldn't load invoice settings")));
    api.get("/admin/invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        next_number: parseInt(form.next_number) || 1,
        cgst_rate: parseFloat(form.cgst_rate) || 0,
        sgst_rate: parseFloat(form.sgst_rate) || 0,
        igst_rate: parseFloat(form.igst_rate) || 0,
      };
      const { data } = await api.put("/admin/invoices/settings", payload);
      setForm(data);
      toast.success("Invoice settings saved — applies to new invoices only");
    } catch (e) {
      toast.error(apiError(e, "Couldn't save invoice settings"));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (field) => async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/uploads", fd);
      setForm((cur) => ({ ...cur, [field]: data.url }));
      toast.success("Image uploaded — save settings to apply");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    }
  };

  const download = async (inv) => {
    setDownloading(inv.id);
    try {
      await downloadInvoicePdf(inv.order_id);
    } catch (e) {
      toast.error(apiError(e, "Couldn't download the invoice"));
    } finally {
      setDownloading("");
    }
  };

  if (!form) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  }

  return (
    <div data-testid="admin-invoices">
      <section className="mb-6 rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-6" data-testid="invoice-settings">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-medium tracking-tight">Company & Tax Details</h3>
          <button data-testid="invoice-settings-save" onClick={save} disabled={saving}
            className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Settings
          </button>
        </div>
        <p className="mt-1 text-xs text-neutral-500">
          Printed on every new invoice. GSTIN, HSN/SAC and tax rates are placeholders until confirmed with PAQVERO's CA — existing invoices never change.
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Company name" testid="inv-company-name" value={form.company_name} onChange={set("company_name")} />
          <Field label="GSTIN" testid="inv-gstin" value={form.gstin} onChange={set("gstin")} placeholder="Pending CA confirmation" hint="15-character GSTIN — leave blank until confirmed" />
          <Field label="HSN / SAC (default)" testid="inv-hsn" value={form.hsn_sac} onChange={set("hsn_sac")} placeholder="e.g. 4819" hint="Shown on invoice line items when set" />
          <Field label="Address line 1" testid="inv-addr1" value={form.address_line1} onChange={set("address_line1")} placeholder="Building, street" />
          <Field label="Address line 2" testid="inv-addr2" value={form.address_line2} onChange={set("address_line2")} placeholder="Area, landmark" />
          <Field label="City" testid="inv-city" value={form.city} onChange={set("city")} />
          <Field label="State" testid="inv-state" value={form.state} onChange={set("state")} hint="Determines CGST+SGST (same state) vs IGST (other state)" />
          <Field label="PIN" testid="inv-pin" value={form.pin} onChange={set("pin")} />
          <Field label="Country" testid="inv-country" value={form.country} onChange={set("country")} />
          <Field label="Phone" testid="inv-phone" value={form.phone} onChange={set("phone")} />
          <Field label="Email" testid="inv-email" value={form.email} onChange={set("email")} />
          <Field label="Website" testid="inv-website" value={form.website} onChange={set("website")} placeholder="www.paqvero.com" />
          <Field label="Brand statement (footer)" testid="inv-brand-statement" value={form.brand_statement} onChange={set("brand_statement")} placeholder="Sourced Smart. Delivered Right." />
        </div>

        <div className="mt-6 border-t border-white/[0.07] pt-5">
          <h4 className="text-sm font-semibold text-white">Payment Details (printed on invoices)</h4>
          <p className="mt-0.5 text-[11px] text-neutral-600">Leave blank until confirmed — blank fields print as "To be updated". A UPI QR is generated automatically when a UPI ID is set.</p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="UPI ID" testid="inv-upi" value={form.upi_id} onChange={set("upi_id")} placeholder="name@bank" />
            <Field label="Bank name" testid="inv-bank-name" value={form.bank_name} onChange={set("bank_name")} />
            <Field label="Account number" testid="inv-bank-account" value={form.bank_account} onChange={set("bank_account")} />
            <Field label="IFSC" testid="inv-bank-ifsc" value={form.bank_ifsc} onChange={set("bank_ifsc")} />
            <Field label="Branch" testid="inv-bank-branch" value={form.bank_branch} onChange={set("bank_branch")} />
            <Field label="Account name" testid="inv-bank-account-name" value={form.bank_account_name} onChange={set("bank_account_name")} />
          </div>
          <div className="mt-4 flex flex-wrap gap-6">
            {[
              ["signature_path", "Signature image", "inv-signature"],
              ["stamp_path", "Company stamp / seal", "inv-stamp"],
            ].map(([field, label, tid]) => (
              <div key={field}>
                <label className={labelCls}>{label}</label>
                <div className="flex items-center gap-3">
                  <label data-testid={`${tid}-upload`} className="flex cursor-pointer items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-xs font-bold text-white transition-colors hover:border-amber-500/60 hover:text-amber-400">
                    <ImagePlus className="h-3.5 w-3.5" /> Upload
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadImage(field)} />
                  </label>
                  {form[field] ? (
                    <>
                      <img src={form[field]} alt={label} className="h-10 w-10 rounded-lg border border-white/10 object-contain bg-white" />
                      <button data-testid={`${tid}-clear`} onClick={() => setForm((f2) => ({ ...f2, [field]: "" }))} className="text-xs text-red-400 hover:underline">Remove</button>
                    </>
                  ) : (
                    <span className="text-[11px] text-neutral-600">Not configured — invoice stays unsigned</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 border-t border-white/[0.07] pt-5">
          <h4 className="text-sm font-semibold text-white">Numbering & Tax Rates</h4>
          <button
            type="button"
            data-testid="inv-gst-enabled"
            aria-pressed={!!form.gst_enabled}
            onClick={() => setForm((f) => ({ ...f, gst_enabled: !f.gst_enabled }))}
            className={`mt-3 flex w-full items-center justify-between rounded-xl border p-4 transition-colors ${
              form.gst_enabled ? "border-emerald-500/40 bg-emerald-500/[0.05]" : "border-amber-500/30 bg-amber-500/[0.04]"
            }`}
          >
            <span className="text-left">
              <span className={`block text-sm font-bold ${form.gst_enabled ? "text-emerald-400" : "text-amber-400"}`}>
                {form.gst_enabled ? "GST charging ON" : "Pre-GST mode — GST not charged"}
              </span>
              <span className="mt-0.5 block text-[11px] text-neutral-500">
                {form.gst_enabled
                  ? "GST is charged per product rate and shown on invoices."
                  : "PAQVERO is not GST-registered yet: no GST is charged or displayed. Product rates/HSN stay saved and activate when you enable this. Applies to new orders only."}
              </span>
            </span>
            <span className={`ml-4 h-6 w-11 shrink-0 rounded-full p-0.5 transition-colors ${form.gst_enabled ? "bg-emerald-500" : "bg-white/10"}`}>
              <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${form.gst_enabled ? "translate-x-5" : ""}`} />
            </span>
          </button>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Field label="Invoice prefix" testid="inv-prefix" value={form.invoice_prefix} onChange={set("invoice_prefix")} />
            <Field label="Next invoice no." testid="inv-next-number" type="number" min="1" value={form.next_number} onChange={set("next_number")} hint={`Next: ${form.invoice_prefix}-${String(parseInt(form.next_number) || 1).padStart(4, "0")}`} />
            <Field label="CGST %" testid="inv-cgst" type="number" min="0" step="0.5" value={form.cgst_rate} onChange={set("cgst_rate")} />
            <Field label="SGST %" testid="inv-sgst" type="number" min="0" step="0.5" value={form.sgst_rate} onChange={set("sgst_rate")} />
            <Field label="IGST %" testid="inv-igst" type="number" min="0" step="0.5" value={form.igst_rate} onChange={set("igst_rate")} />
          </div>
          <div className="mt-4">
            <label className={labelCls}>Terms note (invoice footer)</label>
            <textarea data-testid="inv-terms" rows={2} value={form.terms_note} onChange={set("terms_note")}
              className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none" />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA]" data-testid="invoice-register">
        <div className="flex items-center justify-between border-b border-white/[0.07] px-5 py-4">
          <h3 className="font-display text-lg font-medium tracking-tight">Invoice Register</h3>
          <p className="text-xs text-neutral-500">{invoices ? `${invoices.length} invoices` : "Loading…"}</p>
        </div>
        {!invoices ? (
          <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-amber-500" /></div>
        ) : invoices.length === 0 ? (
          <p className="py-12 text-center text-sm text-neutral-500" data-testid="invoices-empty">No invoices yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm" data-testid="invoices-table">
              <thead>
                <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  <th className="px-5 py-4">Invoice</th>
                  <th className="px-5 py-4">Order</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Customer</th>
                  <th className="px-5 py-4">Payment</th>
                  <th className="px-5 py-4 text-right">Total</th>
                  <th className="px-5 py-4 text-right">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-white/[0.04]" data-testid={`admin-invoice-row-${inv.invoice_number}`}>
                    <td className="px-5 py-3.5 font-mono font-bold text-white">{inv.invoice_number}</td>
                    <td className="px-5 py-3.5 text-neutral-300">
                      {inv.order_number}
                      {inv.stamp && <span className="ml-2 rounded border border-red-500/40 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-400">{inv.stamp}</span>}
                    </td>
                    <td className="px-5 py-3.5 text-neutral-400">{fmtDate(inv.invoice_date)}</td>
                    <td className="px-5 py-3.5 text-neutral-400">{inv.customer_name}<span className="block text-[11px] text-neutral-600">{inv.customer_email}</span></td>
                    <td className="px-5 py-3.5"><PaymentChip ps={inv.payment_status} /></td>
                    <td className="px-5 py-3.5 text-right font-semibold text-white">{fmt(inv.grand_total)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex justify-end">
                        <button data-testid={`admin-invoice-dl-${inv.invoice_number}`} onClick={() => download(inv)} disabled={downloading === inv.id}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors hover:border-amber-500/50 hover:text-amber-400" aria-label="Download PDF">
                          {downloading === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
